import axios from 'axios';

// Public configuration only. Session cookies require the frontend's same-origin proxy.
export const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
export const TOOLS_PATH = (process.env.REACT_APP_TOOLS_API_PATH || '/v1').replace(/\/$/, '');
export const readCsrf = () => {
  const cookie = document.cookie.split('; ').find(value => value.startsWith('cobygo_csrf='));
  return cookie ? decodeURIComponent(cookie.slice('cobygo_csrf='.length)) : '';
};

function validPrincipal(user, type) {
  const roles = user?.roles;
  return user?.id != null && Array.isArray(roles) && (type === 'platform'
    ? user.tenantId == null && roles.includes('COBYGO_ADMIN')
    : user.tenantId != null && !roles.includes('COBYGO_ADMIN') && roles.some(r => ['USER', 'TENANT_ADMIN'].includes(r)));
}

export function createSession(type, { baseURL = API_BASE, adapter, csrf = readCsrf } = {}) {
  if (!['tenant', 'platform'].includes(type)) throw new Error('Invalid session type');
  const publicHttp = axios.create({ baseURL, adapter, withCredentials: true, timeout: 15000 });
  const api = axios.create({ baseURL, adapter });
  const endpoint = type === 'platform' ? '/auth/platform/session' : '/auth/session';
  let token = null; // Never exposed through React context or persisted.
  let generation = 0;
  let revision = 0;
  let refreshFlight = null;
  let restoreFlight = null;
  let logoutFlight = null;
  let snapshot = { status: 'loading', user: null, type, reason: null };
  const listeners = new Set();
  const publish = next => { snapshot = { ...snapshot, ...next }; listeners.forEach(fn => fn()); };
  const clear = reason => {
    generation += 1; token = null; revision += 1;
    publish({ status: 'anonymous', user: null, reason });
  };
  // Avoid accidentally disclosing bearer credentials to an arbitrary URL or the other API area.
  api.interceptors.request.use(config => {
    const origin = window.location.origin;
    const base = new URL((config.baseURL || '') + '/', origin);
    const url = new URL(config.url, base);
    if (config.baseURL !== baseURL || url.origin !== base.origin || /^(?:https?:)?\/\//i.test(config.url)
        || (type === 'tenant' && /\/api\/platform(?:\/|$)/.test(url.pathname))
        || (type === 'platform' && !/\/api\/platform(?:\/|$)/.test(url.pathname))) {
      throw new Error('Request outside session scope');
    }
    if (!token || (config._sessionGeneration != null && config._sessionGeneration !== generation)) throw new Error('Authentication required');
    config.headers.set('Authorization', `Bearer ${token}`);
    config._sessionGeneration ??= generation;
    config._tokenRevision = revision;
    return config;
  });

  const requireSameOrigin = () => {
    if (new URL(baseURL || '/', window.location.origin).origin !== window.location.origin) {
      throw new Error('Browser sessions require a same-origin API proxy');
    }
  };
  const cookieOptions = () => {
    requireSameOrigin();
    const value = csrf();
    if (!value) throw new Error('Session CSRF cookie unavailable');
    return { headers: { 'X-Cobygo-CSRF': value } };
  };
  const establish = async (data, epoch) => {
    if (epoch !== generation) throw new Error('Session changed');
    if (!data?.accessToken || data.tokenType !== 'Bearer' || !validPrincipal(data.user, type)) {
      throw new Error('Invalid session response');
    }
    let user = data.user;
    if (type === 'tenant') {
      // Use the authoritative profile endpoint, without the refresh interceptor.
      const result = await publicHttp.get('/api/users/me', {
        withCredentials: false, headers: { Authorization: `Bearer ${data.accessToken}` }
      });
      user = result.data;
      if (!validPrincipal(user, type) || user.id !== data.user.id || user.tenantId !== data.user.tenantId) {
        throw new Error('Invalid tenant principal');
      }
    }
    if (epoch !== generation) throw new Error('Session changed');
    token = data.accessToken; revision += 1;
    publish({ status: 'authenticated', user, reason: null });
    return user;
  };
  const refresh = () => {
    if (refreshFlight) return refreshFlight;
    const epoch = generation;
    refreshFlight = Promise.resolve().then(() => publicHttp.post(`${endpoint}/refresh`, undefined, cookieOptions()))
      .then(result => establish(result.data, epoch))
      .catch(error => { if (epoch === generation) clear('expired'); throw error; })
      .finally(() => { refreshFlight = null; });
    return refreshFlight;
  };
  api.interceptors.response.use(response => response, async error => {
    const config = error.config;
    if (error.response?.status !== 401 || !config || config._sessionGeneration !== generation) throw error;
    if (config._sessionRetried) { clear('expired'); throw error; }
    if (config.signal?.aborted) throw error;
    config._sessionRetried = true;
    // A delayed 401 for an older access token can reuse an already-completed refresh.
    if (config._tokenRevision === revision) await refresh();
    if (config._sessionGeneration !== generation || !token) throw error;
    return api.request(config);
  });
  // Existing tool pages log errors. Never pass Axios's credential-bearing config/request
  // into those consumers; preserve their response data/status and cancellation checks.
  const safeError = error => {
    const clean = new Error(error.response ? 'API request failed' : 'Unable to reach the service');
    clean.code = error.code;
    if (error.response) clean.response = { status: error.response.status, data: error.response.data };
    return Promise.reject(clean);
  };
  api.interceptors.response.use(response => response, safeError);
  return {
    type, api,
    subscribe: fn => { listeners.add(fn); return () => listeners.delete(fn); },
    getSnapshot: () => snapshot,
    restore() {
      // StrictMode and multiple guards share a single startup attempt.
      if (!restoreFlight) restoreFlight = snapshot.status === 'authenticated'
        ? Promise.resolve(snapshot.user) : refresh().catch(() => {
          if (snapshot.reason === 'expired') publish({ reason: null });
          return null;
        });
      return restoreFlight;
    },
    async login(email, password) {
      if (logoutFlight) await logoutFlight.catch(() => {});
      // Do not race a cookie-rotating restoration against login.
      if (refreshFlight) await refreshFlight.catch(() => {});
      clear(null); const epoch = generation;
      publish({ status: 'authenticating' });
      try {
        requireSameOrigin();
        const result = await publicHttp.post(`${endpoint}/login`, { email, password });
        return await establish(result.data, epoch);
      } catch (error) { if (epoch === generation) clear('login_failed'); return safeError(error); }
    },
    logout() {
      if (logoutFlight) return logoutFlight;
      const pending = refreshFlight;
      clear('signed_out');
      // Wait for an in-flight rotation before revoking the cookie's current family.
      logoutFlight = Promise.resolve(pending).catch(() => {}).then(() => publicHttp.post(`${endpoint}/logout`, undefined, cookieOptions()))
        .catch(() => { publish({ reason: 'logout_failed' }); throw new Error('Server sign-out could not be confirmed. Close this browser session before sharing this device.'); })
        .finally(() => { logoutFlight = null; });
      return logoutFlight;
    },
    refresh,
    discover: email => publicHttp.post('/auth/discover', { email }).then(r => r.data),
    startSso: (email, route) => publicHttp.get('/auth/sso/start', { params: { email, route } }).then(r => r.data),
    setupPassword: (setupToken, password) => publicHttp.post('/auth/password/setup', { token: setupToken, password }),
    acceptInvitation: (token, password, displayName) => publicHttp.post('/auth/invitations/accept', { token, password, displayName: displayName ? displayName.trim() : undefined }),
  };
}

export const tenantSession = createSession('tenant');
export const platformSession = createSession('platform');
